import _ from "lodash"
import { OPERATORS, CONVERSIONS } from "."
import { compareData } from "./compareData"
import { randoms } from "./randomizers"

/*
  ===============================================================================
  This doc evalautes any single conditional statement and returns a verdict.
  Process:

  - the evaluateConditionalItem() method is called. It must receive:
      clientApiData: the rp and merch data sent in from MM as a single object; ie: { rp: {}, merch: {} }
      itemReportObj: the conditional object itself; which has all required data from our internal apidata collection 

  - the lookupApiValue() method is called to retrieve the data from the rp or merch api feed.

  - if any conversions needs to be applied to the data, the conversion method is applied to the data.

  - the compareData() method is called to evaluate the data against the expected value using the specified operator.
    this method sends back the actual data found, and the verdict of the comparison.

  ===============================================================================
 */


const defaultConditional = {
  "source_id": null,
  name: "",
  description: "",
  clipboard: [],
  items: []
}
  
const makeNewConditional = () => {
  const newZoneRef = randoms.string(12, "a#")
  const newZone = { zoneref: newZoneRef, groups: [] }
  return {...defaultConditional, zones: [newZone]}
}

function getOperatorLabel(value:string){
  const operator = OPERATORS.find( (op:any) => op.value === value )
  return operator ? operator.label : ""
}

function getOperatorDef(value:string){
  const operator = OPERATORS.find( (op:any) => op.value === value )
  return operator ? operator.def : ""
}

function replaceAllParts(part:string, idx:number, length:number){  
  if( idx === 1 || idx === length -1 ){
    const arr = part.match(/[A-Z][a-z]+/g);
    let newStr = ""
    arr?.map( (str:string) => {
      newStr += replaceWords[str] ? replaceWords[str] : str.substring(0, 3) 
    })
    return newStr
  } else {
    return ""
  }
}

function abbreviationBuilder(fullPath:string){
  const parts = fullPath.split(".")
  let abbreviation = ""
  parts.map( (part:string, idx:number) => {
    if( idx === 0 ) {
      abbreviation += `${part.split("")[0].toUpperCase()}${part.split("").slice(1).join("")}`
    } else {
      abbreviation += replaceAllParts(part, idx, parts.length)
    }
  })
  return abbreviation
}


function getFullParentPath(family:string, tree:any, parent:string, path:string):string{
  if( parent === "" || parent === null ) return `${family}.${path}`
  let fullPath = path 
  const parentObj = tree.find( (item:any) => item?.refid === parent )
  if( !parentObj ) return fullPath
  fullPath = `${parentObj.name}.${fullPath}`
  return getFullParentPath(family, tree, parentObj.parent, fullPath)
}

/** === retrieves specific data item from the rp api feed === */
const getRepresentmentData = ({ data, path }:{ data:any; path:string }) => {
  if( path.startsWith("rp.") ){
    path = path.split("rp.")[1]
  }
  return _.get(data, path)
}

/** === retrieves specific data item from the merchant api feed === */
const getMerchantData = ({ data, path }:{ data:any; path:string }) => {
  if( path.startsWith("merchant.") ){
    path = path.split("merchant.")[1]
  }

  if( path.startsWith("merch.") ){
    path = path.split("merch.")[1]
  }

  const result = _.get(data, path)
  return result
}

/** === utility data fetchers === */
const getFromRp = (data:any, path:string) => getRepresentmentData({ data, path })
const getFromMerch = (data:any, path:string) => getMerchantData({ data, path })
const getApiFeedData = (data:any, path:string) => (path.startsWith("rp.")) ? getFromRp(data.rp, path) : getFromMerch(data.merchant, path)


/** === returns the client api data matching a specified path === */
function lookupApiValue(clientApiData:any, apiValueObj:any){
  // console.log("apiValueObj", apiValueObj)
  // console.log("clientApiData", clientApiData)
  const data = getApiFeedData(clientApiData, apiValueObj.path)
  return data
}


function evaluateConditionalObj(conditional:any, clientApiData:any){
  const showReports = true;

  const report = {
    verdict: true,
    zones: [] as {
      zoneref: string;
      groups: { passed: string[]; failed: string[] };
      items: { passed: string[]; failed: string[] };
    }[],
  };

  if (!conditional?.zones || !Array.isArray(conditional.zones)) {
    return { verdict: false, zones: [] };
  }

  for (const zone of conditional.zones) {
    const zoneReport = {
      zoneref: zone.zoneref,
      groups: { passed: [] as string[], failed: [] as string[] },
      items: { passed: [] as string[], failed: [] as string[] },
    };

    let zonePassed = true;

    for (const group of zone.groups || []) {
      let groupPassed = false;

      for (const itemRef of group.items || []) {
        const item = conditional.items.find((itm: any) => itm.refid === itemRef);

        if (item) {
          const result = evaluateConditionalItem(clientApiData, item); // << Now calls the imported evaluator directly!

          if (result) {
            groupPassed = true;
            zoneReport.items.passed.push(item.refid);
          } else {
            zoneReport.items.failed.push(item.refid);
          }
        } else {
          // Item ref not found — treat as failed
          zoneReport.items.failed.push(itemRef);
        }
      }

      if (groupPassed) {
        zoneReport.groups.passed.push(group.groupref);
      } else {
        zoneReport.groups.failed.push(group.groupref);
        zonePassed = false;
      }
    }

    if (!zonePassed) {
      report.verdict = false;
    }

    report.zones.push(zoneReport);
  }

  return report;
};





function evaluateConditionalOld(conditionalObj:any, clientApiData:any){

  const showReports = true; 

  let base_report:any = {
    zoneref: null,
    verdict: false,
    groups: { passed: [], failed: [] },
    items: { passed: [], failed: [] },
  }

  let final_report:any = {
    zones: []
  }

  conditionalObj.zones?.forEach( (zone:any, idx:number) => {
    if(showReports) console.log(`evaluating zone ${zone.zoneref}`)

    let report = _.cloneDeep({...base_report, zoneref: zone.zoneref })
    final_report = {
      ...final_report,
      zones: [
        ...final_report.zones, 
        { 
          zoneref: zone.zoneref, 
          groups: { passed: [], failed: [] }, 
          items: { passed: [], failed: [] }
        }] 
      }
    
    zone.groups.forEach( (group:any) => {
      group.items.forEach( (item:any, idx:number) => {
        const itemObj = conditionalObj.items.find( (i:any) => i.refid === item )
        const verdict = evaluateConditionalItem(clientApiData, itemObj)
        if(showReports) console.log(`evaluated item ${itemObj.refid} in group ${group.groupref}:`, verdict)
        if( verdict ){
          report.items.passed.push(itemObj)
          if(showReports) console.log(`zone ${zone.zoneref} group ${group.groupref} item ${itemObj.refid} has ${report.items.passed.length} passes`)
        } else {
          report.items.failed.push(itemObj)
          if(showReports) console.log(`zone ${zone.zoneref} group ${group.groupref} item ${itemObj.refid} has ${report.items.failed.length} fails`)
        }
      })

      // only one item in a group must pass for the group to pass
      if( report.items.passed.length > 0 ){
        report.groups.passed.push(group.groupref)
        console.log(`zone ${zone.zoneref} group ${group.groupref} has ${report.groups.passed.length} passes`)
      } else {
        report.groups.failed.push(group.groupref)
      }
    })

    let zoneVerdict:boolean = false
    // all groups for the current zone are done; all must pass for the zone to pass
    if( report.groups.passed.length > 0 && report.groups.failed.length === 0 && report.groups.passed.length === zone.groups.length ){
      zoneVerdict = true
    }

    if( zoneVerdict ){
      report.verdict = true
      console.log(`zone ${zone.zoneref} has passed`)
    } else {
      console.log(`zone ${zone.zoneref} has failed`)
    }

    console.info(`=========================================
FINAL VERDICT FOR ZONE ${zone.zoneref}:  ${zoneVerdict} with ${report.groups.passed.length} passed groups and ${report.groups.failed.length} failed groups
=========================================`)
  final_report = [...final_report.zones, report]

  })

  console.log(final_report)
  return final_report

}




function evaluateConditionalItem(clientApiData:any, itemReportObj:any){
  let dataToEvaluate = lookupApiValue(clientApiData, itemReportObj.apiValueObj)

  if( itemReportObj.conversion !== undefined && itemReportObj.conversion !== "none" ){
    const conversion:any = CONVERSIONS.find( (conv:any) => conv.value === itemReportObj.conversion )
    if( conversion ){
      // console.log("converting data")
      dataToEvaluate = conversion.method(dataToEvaluate, itemReportObj.value)
      // console.log("converted data", dataToEvaluate)
    }
  }

  let expectedValue = itemReportObj.value
  if( typeof itemReportObj.value === "string" && itemReportObj.value.includes(":") ){
    // this item is part of a conversion and only the part after the ":" is used for the comparison
    expectedValue = itemReportObj.value.split(":")[1]
  }

  const operator = itemReportObj.operator 


  const { verdict }:any = compareData(dataToEvaluate, expectedValue, operator)

  // console.log(`evaluating:
  //   dataEvaluated: ${dataToEvaluate}
  //   expectedValue: ${expectedValue}
  //   operator: ${operator}
  //   result: ${verdict}
  // `)
  
  return verdict
}

const replaceWords:any = {
  "Description": "Desc",
  "Amount": "Amt",
  "Currency": "Cur",
  "Dispute": "Dsp",
  "Account": "Acn",
  "Chargeback": "CB",
  "Transaction": "Trx",
  "Category": "Cat",
  "Cycle": "Cyc",
  "Date": "Dt",
  "Number": "Num",
  "Name": "Nm",
  "Consumer": "Csmr",
  "Billing": "Bill",
  "Shipping": "Shp",
  "Address": "Adr",
  "Reference": "Ref",
  "ReasonCode": "RC",
  "Current": "Crt",
  "Action": "Act",
  "Source": "Src",
  "Processor": "Prc",
  "Manage": "Mng",
  "Choice": "Chc",
  "User": "Usr",
  "Order": "Or",
  "Info": "In",
  "Document": "Doc",
  "Documents": "Docs",
  "Additional": "Addt",
  "Between": "Btn",
  "Cancellation": "Cxl",
  "Refund": "Rfnd",
  "Receipt": "Rcpt",
}



export {
  abbreviationBuilder,
  getFullParentPath,
  getOperatorDef,
  getOperatorLabel,
  evaluateConditionalObj,
  evaluateConditionalItem,
  makeNewConditional,
  replaceWords
}


